// Isolates Store Action query caching and render failures from the surrounding dashboard.
import React, { PropsWithChildren, useState } from 'react';
import { QueryClient, QueryErrorResetBoundary } from '@tanstack/react-query';
import {
  PersistQueryClientProvider,
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client';
import { get, set, del } from 'idb-keyval';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Button,
  Card,
  CardContent,
  makeStyles,
  Typography,
} from '@rentacenter/racstrap';

import { STORE_ACTION_WIRE_STATUS } from './storeActionDomain';
import { getStoreActionMessage } from './storeActionLocalization';

type StoreActionFeatureBoundaryProps = PropsWithChildren<{
  readonly children?: React.ReactNode;
}>;

const useStyles = makeStyles({
  card: {
    backgroundColor: '#fff',
    border: 0,
    borderRadius: 22,
    boxShadow: '0 3px 6px rgba(14, 58, 147, 0.102)',
    height: 271,
    width: '100%',
  },
  content: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    padding: 24,
    textAlign: 'center',
  },
  icon: {
    alignItems: 'center',
    backgroundColor: '#fce8ed',
    borderRadius: '50%',
    color: '#c9344f',
    display: 'flex',
    fontSize: 16,
    fontWeight: 700,
    height: 32,
    justifyContent: 'center',
    lineHeight: 1,
    marginBottom: 12,
    width: 32,
  },
  title: {
    color: '#172b4d',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '23px',
  },
  message: {
    color: '#576c7b',
    fontSize: 14,
    lineHeight: '20px',
    marginTop: 4,
  },
  retry: {
    marginTop: 16,
  },
});

const createStoreActionQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        cacheTime: 1000 * 60 * 60 * 24 * 7, // 1 week
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    },
  });

const createQueryClientPersister = (
  idbValidKey: IDBValidKey = 'reactQuery'
): Persister => ({
  persistClient: async (client: PersistedClient) => {
    await set(idbValidKey, {
      ...client,
      clientState: {
        ...client.clientState,
        queries: client.clientState.queries.map((query) => {
          const data = query.state.data as
            | { pages?: unknown; pageParams?: unknown }
            | undefined;
          // Dehydration omits query options, so identify the infinite list by its key.
          if (
            query.queryKey.length === 3 &&
            query.queryKey[0] === 'store-actions' &&
            (query.queryKey[2] === STORE_ACTION_WIRE_STATUS.active ||
              query.queryKey[2] === STORE_ACTION_WIRE_STATUS.completed) &&
            data &&
            Array.isArray(data.pages) &&
            Array.isArray(data.pageParams)
          ) {
            return {
              ...query,
              state: {
                ...query.state,
                data: {
                  ...data,
                  pages: data.pages.slice(0, 1),
                  pageParams: data.pageParams.slice(0, 1),
                },
              },
            };
          }
          return query;
        }),
      },
    });
  },
  restoreClient: async () => {
    return await get<PersistedClient>(idbValidKey);
  },
  removeClient: async () => {
    await del(idbValidKey);
  },
});

export const StoreActionFeatureBoundary: React.FC<StoreActionFeatureBoundaryProps> =
  ({ children }) => {
    const [queryClient] = useState(createStoreActionQueryClient);
    const [persister] = useState(createQueryClientPersister);
    const classes = useStyles();

    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}
        onSuccess={() => {
          queryClient.invalidateQueries({ refetchType: 'none' });
        }}
      >
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              onReset={reset}
              fallbackRender={({ resetErrorBoundary }) => (
                <Card
                  aria-live="assertive"
                  className={classes.card}
                  role="alert"
                >
                  <CardContent className={classes.content}>
                    <span aria-hidden="true" className={classes.icon}>
                      !
                    </span>
                    <Typography className={classes.title} component="h2">
                      {getStoreActionMessage('unableToLoad')}
                    </Typography>
                    <Typography className={classes.message} component="p">
                      {getStoreActionMessage('tryAgainSoon')}
                    </Typography>
                    <Button
                      className={classes.retry}
                      color="primary"
                      onClick={resetErrorBoundary}
                      size="small"
                      type="button"
                      variant="outlined"
                    >
                      {getStoreActionMessage('retry')}
                    </Button>
                  </CardContent>
                </Card>
              )}
            >
              {children}
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </PersistQueryClientProvider>
    );
  };
